import { profileReturnVerificationCables } from "./design-profiles/coverage";
import { resolveDesignProfile } from "./design-profiles";
import { computeBatteryBankCurrents } from "./battery-bank";
import { computeCableSpec, getEffectiveCableFuse, type CableSpec, type SimulateContext } from "./cable-current";
import { getDefinition, type DefLookup } from "./catalog";
import { computeFridgeInstallationPolicies } from "./fridge-installation-policy";
import { computeCrankingCables, computeKnownStartingCurrentCables } from "./cranking-circuit";
import { computeCableDemandAnalysis } from "./cable-demands";
import { controlSizingCableIds } from "./control-sizing";
import { isFixedControlDesignScenario } from "./diagnostics/control-design-states";
import { indexNodesById } from "./node-tree";
import { computePathMinWireSizes } from "./path-vdrop-budget";
import { DEFAULT_AUTO_SIZE_VDROP_TARGET } from "./settings/auto-size-vdrop-default";
import type { Cable, System } from "./types";
import { computeEnergizedTerminals } from "./diagnostics/graph";
import { computeVdropAllowances } from "./vdrop-allowance";
import { dcBreakerConductorRatings } from "./dc-breaker-conductors";
import { applyFuseBoardCableLimits, fuseBoardWireMinimum } from "./fuse-board-limits";
import { computeInstallationRuns, protectiveGroundVerificationCables } from "./installation-limits";
import { fuseExceedsTerminalLimit } from "./terminal-fuse-limit";

/**
 * Recompute explicitly automatic fuses; manual and legacy declarations remain
 * unchanged, and an unfused cable stays unfused. Returns the same array and
 * cable objects where nothing changed — React Flow reconciles edges by identity.
 */
export function refreshAutoFuses(
  system: System,
  defLookup: DefLookup = getDefinition,
  autoSizeTargetPct: number = DEFAULT_AUTO_SIZE_VDROP_TARGET,
): Cable[] {
  const hasFusedCable = system.cables.some((c) => c.fuseAmpOverride != null);
  if (!hasFusedCable) return system.cables;
  // Remove only cached automatic selections. Manual and legacy declarations
  // remain real protection constraints while sizing other automatic cables;
  // a fuse is never a measurement of operating current.
  const bare: System = {
    ...system,
    cables: system.cables.map((c) =>
      c.fuseAmpOverride != null && c.fuseAmpAuto === true ? { ...c, fuseAmpOverride: null } : c,
    ),
  };
  const specOf = makeSystemCableSpecGetter(bare, defLookup, undefined, autoSizeTargetPct);
  let changed = false;
  const next = system.cables.map((c, i) => {
    if (c.fuseAmpOverride == null) return c;
    const bareCable = bare.cables[i];
    const spec = specOf(bareCable);
    // A rating an earlier connect flow left on an AC run (a 1 A fuse on a
    // shore neutral) is dropped, not refreshed: the inlet's breaker is that
    // conductor's protection and the inspector no longer offers a fuse there.
    if (spec.isAc && !spec.sizingVerificationRequired) {
      changed = true;
      return { ...c, fuseAmpOverride: null };
    }
    // Missing legacy provenance is not permission to replace an installed pick.
    if (c.fuseAmpAuto !== true) return c;
    // A selected switch position is not the all-position current envelope.
    // Keep the cached choice visible as provisional, without refreshing it
    // into seemingly approved automatic advice from that single position.
    if (spec.currentBasis === "provisional-fallback" || spec.sizingVerificationRequired) return c;
    // Keep the saved declaration visible for diagnostics when its selected
    // family cannot support fresh advice; never silently switch fuse families.
    if (spec.fuseRecommendationUnavailableReason || fuseExceedsTerminalLimit(spec, c.fuseAmpOverride)) return c;
    // 0 is "nothing to rate against" — a positive run whose ends resolve no
    // current. Unlike AC, a fuse there is not wrong, only unsized, so the rating
    // already on the cable stays rather than being nulled or replaced with 0.
    const rating = spec.manufacturerFuseA ?? spec.fuse;
    if (rating <= 0 || fuseExceedsTerminalLimit(spec, rating)) return c;
    if (rating === c.fuseAmpOverride) return c;
    changed = true;
    return { ...c, fuseAmpOverride: rating };
  });
  return changed ? next : system.cables;
}

/** Build once per system/state snapshot. All editor, diagnostic and export
 * consumers size through this boundary so none can omit a physical or policy
 * input. A modified cable with the same id may change its local overrides;
 * topology/length edits require a new system and a new getter. */
export function makeSystemCableSpecGetter(
  system: System,
  defLookup: DefLookup = getDefinition,
  simulate?: SimulateContext,
  autoSizeTargetPct: number = DEFAULT_AUTO_SIZE_VDROP_TARGET,
): (cable: Cable) => CableSpec {
  if (simulate && !simulate.energizedTerminals) simulate = {
    ...simulate,
    energizedTerminals: computeEnergizedTerminals(system, defLookup, simulate),
  };
  const designProfile = resolveDesignProfile(system);
  const unverifiedReturns = designProfile.policy.coverage.chassisReturn ? new Set<string>() : profileReturnVerificationCables(system, defLookup);
  const unverifiedProtectiveGrounds = protectiveGroundVerificationCables(system, defLookup);
  const nodesById = indexNodesById(system.nodes);
  const installationRuns = computeInstallationRuns(system, defLookup);
  const installationUnverified = new Set(installationRuns
    .filter(run => run.parallelWiringUnverified || run.verificationReason).flatMap(run => run.cableIds));
  const terminalWireMaximums = new Map(installationRuns.filter(run => run.terminalWireMaximumMm2 != null)
    .map(run => [run.cableIds[0], run.terminalWireMaximumMm2!]));
  const analysis = computeCableDemandAnalysis(system, defLookup, simulate);
  const pathMins = computePathMinWireSizes(system, defLookup, simulate, autoSizeTargetPct);
  const cranking = computeCrankingCables(system, defLookup);
  const startingEvidence = computeKnownStartingCurrentCables(system, defLookup);
  const allowances = computeVdropAllowances(system, defLookup, autoSizeTargetPct);
  const bank = computeBatteryBankCurrents(system, defLookup, simulate);
  const manufacturerPolicies = computeFridgeInstallationPolicies(system, defLookup).byCable;
  const breakerRatings = dcBreakerConductorRatings(system, defLookup);
  const controlUnverified = !simulate && !isFixedControlDesignScenario(system)
    ? controlSizingCableIds(system, defLookup) : new Set<string>();
  return cable => {
    let spec = computeCableSpec(
      cable,
      nodesById,
      system.cables,
      defLookup,
      simulate,
      analysis.demands.get(cable.id) ?? (analysis.fallbackCableIds.has(cable.id)
        ? { continuous: 0, peak: 0, currentBasis: "provisional-fallback" } : undefined),
      Math.max(pathMins.get(cable.id) ?? 0, fuseBoardWireMinimum(cable, nodesById, defLookup)),
      cranking.has(cable.id),
      allowances.byCable.get(cable.id),
      bankCurrentMagnitude(bank.byCable.get(cable.id)),
      startingEvidence.has(cable.id),
      manufacturerPolicies.get(cable.id),
      breakerRatings.get(cable.id)?.ratingA,
      designProfile,
    );
    if (unverifiedReturns.has(cable.id)) spec = { ...spec, designProfileId: designProfile.id, sizingVerificationRequired: true,
      sizingVerificationReasons: [...(spec.sizingVerificationReasons ?? []), "chassis-return-unverified"] };
    spec = applyFuseBoardCableLimits(cable, nodesById, defLookup, spec);
    if (installationUnverified.has(cable.id) || spec.wire.mm2 > (terminalWireMaximums.get(cable.id) ?? Infinity)) spec = { ...spec,
      manufacturerInstallationUnverified: true,
      fuseRecommendationUnavailableReason: "manufacturer-installation",
      fuse: getEffectiveCableFuse(cable, nodesById, defLookup).ratingA ?? 0,
    };
    // A protective conductor is sized for fault duty, not normal DC demand or
    // an AC inlet's operating rating. Preserve a manual wire declaration but
    // withhold verified automatic sizing and any fuse recommendation.
    if (unverifiedProtectiveGrounds.has(cable.id)) spec = { ...spec, fuse: 0,
      designProfileId: designProfile.id, sizingVerificationRequired: true,
      sizingVerificationReasons: [...(spec.sizingVerificationReasons ?? []), "protective-ground-sizing-unverified"] };
    // A DC branch omitted by the demand model can still inherit a legacy
    // terminal/fuse planning estimate. Keep that sizing floor, but never expose
    // it as a verified current or automatic purchase quantity. Annotate after
    // selection so the provenance marker cannot disable the legacy fallback.
    if (!simulate && !spec.isAc && (controlUnverified.has(cable.id) ||
        (spec.currentBasis == null && spec.current > 0))) {
      return { ...spec, currentBasis: "provisional-fallback" };
    }
    return spec;
  };
}

export function computeSystemCableSpec(
  cable: Cable,
  system: System,
  defLookup: DefLookup = getDefinition,
  simulate?: SimulateContext,
  autoSizeTargetPct: number = DEFAULT_AUTO_SIZE_VDROP_TARGET,
): CableSpec {
  return makeSystemCableSpecGetter(system, defLookup, simulate, autoSizeTargetPct)(cable);
}

/** `battery-bank.ts` signs its current for flow direction; sizing wants the magnitude. */
function bankCurrentMagnitude(signed: number | undefined): number | undefined {
  return signed == null ? undefined : Math.abs(signed);
}

/**
 * Resolve every cable using the same demand walk and cumulative path-voltage
 * budget as the canvas. Batch consumers such as BOM and print documents must
 * use this instead of calling `computeCableSpec` without its system hints —
 * doing that can understate a shared trunk's selected conductor size.
 */
export function computeSystemCableSpecs(
  system: System,
  defLookup: DefLookup = getDefinition,
  simulate?: SimulateContext,
  autoSizeTargetPct: number = DEFAULT_AUTO_SIZE_VDROP_TARGET,
): Map<string, CableSpec> {
  const specOf = makeSystemCableSpecGetter(system, defLookup, simulate, autoSizeTargetPct);
  return new Map(system.cables.map(cable => [cable.id, specOf(cable)]));
}
